import { SearchType } from '@brightdrop/bd-ui';
import { Link } from '@mui/material';
import { DateTime } from 'luxon';
import { Trans } from 'react-i18next';
import { Link as RouteLink } from 'react-router-dom';

import type { AlertDto, AlertRecord } from '~/api/generated-api';
import TranslatedLabel, {
  translateText,
} from '~/common/components/TranslatedLabel/TranslatedLabel';
import {
  DEFAULT_EMPTY_VALUE,
  DEFAULT_MONTH_DAY_YEAR_DATE_FORMAT,
  TIRES_THRESHOLD_FLAT,
  TIRES_THRESHOLD_LOW,
} from '~/common/constants/common.constant';
import {
  type AlertDetailsMap,
  AlertEntitiesQueryFieldType,
  AlertEvent,
  AlertQueryFieldType,
  AlertType,
  AlertTypeFilter,
  type ReceiptPreferenceType,
} from '~/common/models/alert.model';
import type { ApiAssetType } from '~/common/models/asset.model';
import {
  DateRange as AlertDateRange,
  type SearchCriteria,
} from '~/common/models/common.model';
import { FLEET_PERIPHERALS_LIST } from '~/common/models/pages/fleetPages.model';
import { SETTINGS_VIEW_PATH } from '~/common/models/pages/settingsPages.model';
import { type BDQuery, BDQueryCriteriaType } from '~/common/models/query.model';
import { RouteName } from '~/common/models/route.model';
import {
  type BuildAssetUrlFn,
  buildAssetUrl as defaultBuildAssetUrl,
} from '~/common/utils/asset-url.utils';
import { format, getDateRangeConfig } from '~/common/utils/date-time.utils';
import { buildQueryTree } from '~/common/utils/query.utils';
import { findRoute, makeRoutePath } from '~/common/utils/route/route.utils';
import type { NOTIFICATION_SETTINGS_MESSAGES } from '~/routes/settings/settings.messages';

import type {
  AlertListFilterSession,
  AlertParams,
} from '~/store/alerts/alertsSlice';
import { getEmerEventMessage } from '~/utils/alerts/emerAlerts.utils';
import UserAlertLabel from './UserAlertLabel';

export const createSearchQuery = (
  {
    nameFieldName,
    idFieldName,
  }: { nameFieldName: string; idFieldName: string },
  searchType: SearchType,
  input: string
): BDQuery | undefined => {
  let criteria: BDQuery | undefined;
  switch (searchType) {
    case SearchType.ASSET: {
      criteria = {
        qualifier: BDQueryCriteriaType.STARTS_WITH,
        field: nameFieldName,
        values: [input],
      } as BDQuery;
      break;
    }
    case SearchType.ID: {
      criteria = {
        qualifier: BDQueryCriteriaType.STARTS_WITH,
        field: idFieldName,
        values: [input],
      } as BDQuery;
      break;
    }
  }
  return criteria;
};

export const mapAlertTypeFilterToAlertType = (option: string): AlertType => {
  if (option === AlertTypeFilter.LOW_BATTERY) {
    return AlertType.LOW_BATTERY;
  }
  if (option === AlertTypeFilter.LOW_BRAKE_FLUID) {
    return AlertType.LOW_BRAKE_FLUID;
  }
  if (option === AlertTypeFilter.LOW_WASHER_FLUID) {
    return AlertType.LOW_WASHER_FLUID;
  }
  if (option === AlertTypeFilter.LOW_TIRE_PRESSURE) {
    return AlertType.LOW_TIRE_PRESSURE;
  }
  return AlertType.FLAT_TIRE;
};

const createContextQuery = (params: Partial<AlertParams>) => {
  return params.assetsId
    ? ({
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field: AlertQueryFieldType.SOURCE_ID,
        values: [params.assetsId],
      } as BDQuery)
    : params.fleetsId
      ? ({
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: AlertQueryFieldType.FLEET_ID,
          values: [params.fleetsId],
        } as BDQuery)
      : params.hubsId
        ? ({
            qualifier: BDQueryCriteriaType.IS_EQUAL,
            field: AlertQueryFieldType.HUB_ID,
            values: [params.hubsId],
          } as BDQuery)
        : params.organizationsId
          ? ({
              qualifier: BDQueryCriteriaType.IS_EQUAL,
              field: AlertQueryFieldType.ORG_ID,
              values: [params.organizationsId],
            } as BDQuery)
          : undefined;
};
const createAssetTypeQuery = (selectedAssetTypes?: ApiAssetType[]) => {
  return selectedAssetTypes?.length
    ? {
        qualifier: BDQueryCriteriaType.IN,
        field: AlertQueryFieldType.SOURCE_TYPE,
        values: selectedAssetTypes,
      }
    : undefined;
};

const createAlertTypeQuery = (selectedAlertTypes?: AlertEvent[]) => {
  return selectedAlertTypes?.length
    ? {
        qualifier: BDQueryCriteriaType.IN,
        field: AlertQueryFieldType.ALERT_TYPE,
        values: selectedAlertTypes,
      }
    : undefined;
};

/**
 * Create query for selected receipt preferences
 * @param selectedReceiptPreferences array of selected receipt preferences
 * @returnsb query for selected receipt preferences
 */
const createAlertReceiptPreferenceQuery = (
  selectedReceiptPreferences?: ReceiptPreferenceType[]
) => {
  return selectedReceiptPreferences?.length
    ? {
        qualifier: BDQueryCriteriaType.ARRAY_CONTAINS,
        field: AlertQueryFieldType.RECEIPT_PREFERENCES,
        values: selectedReceiptPreferences,
      }
    : undefined;
};

export const createDateRangeQuery = (selectedDateRange?: AlertDateRange) => {
  if (!selectedDateRange) {
    return undefined;
  }
  const range = getDateRangeConfig(selectedDateRange);
  return createDateBetweenQuery(AlertQueryFieldType.TIMESTAMP, range);
};
const createFilterSessionQuery = (criteria?: AlertListFilterSession) => {
  return [
    createAssetTypeQuery(criteria?.selectedAssetTypes),
    createAlertTypeQuery(criteria?.selectedAlertTypes),
    createDateRangeQuery(criteria?.selectedDateRange),
    createAlertsSearchQuery(
      {
        idFieldName: AlertQueryFieldType.SOURCE_ID,
        nameFieldName: AlertQueryFieldType.SOURCE_NAME,
      },
      criteria?.searchCriteria
    ),
  ];
};

/**
 * alert query for getting alerts from backend
 * we always need to retrive only FEED alets
 * @param params alert params
 * @param criteria alert filter session
 * @returns alert BDQuery
 */
export const createAlertQuery = (
  params: Partial<AlertParams>,
  criteria?: AlertListFilterSession
): BDQuery | undefined => {
  const queries: BDQuery[] = [
    createContextQuery(params),
    ...createFilterSessionQuery(criteria),
    createAlertReceiptPreferenceQuery(['FEED']),
  ].filter((query) => !!query) as BDQuery[];
  if (!queries?.length) {
    return undefined;
  }
  return buildQueryTree(queries, 0, queries.length - 1, true);
};

const createEntitiesContextQuery = (params: Partial<AlertParams>) => {
  return params.assetsId
    ? ({
        qualifier: BDQueryCriteriaType.IS_EQUAL,
        field: AlertEntitiesQueryFieldType.SOURCE_ID,
        values: [params.assetsId],
      } as BDQuery)
    : params.fleetsId
      ? ({
          qualifier: BDQueryCriteriaType.IS_EQUAL,
          field: AlertEntitiesQueryFieldType.FLEET_ID,
          values: [params.fleetsId],
        } as BDQuery)
      : params.hubsId
        ? ({
            qualifier: BDQueryCriteriaType.IS_EQUAL,
            field: AlertEntitiesQueryFieldType.HUB_ID,
            values: [params.hubsId],
          } as BDQuery)
        : params.organizationsId
          ? ({
              qualifier: BDQueryCriteriaType.IS_EQUAL,
              field: AlertEntitiesQueryFieldType.ORG_ID,
              values: [params.organizationsId],
            } as BDQuery)
          : undefined;
};
export const createDateBetweenQuery = (
  field: string,
  range: {
    start: Date;
    end?: Date;
  }
): BDQuery => {
  return {
    qualifier: BDQueryCriteriaType.BETWEEN,
    field: field,
    values: [
      range.start.toISOString(),
      range.end
        ? range.end.toISOString()
        : DateTime.fromJSDate(range.start)
            .endOf('day')
            .toJSDate()
            .toISOString(),
    ],
  };
};
export const createEntitiesDateRangeQuery = (
  criteria?: AlertListFilterSession
) => {
  const range = getDateRangeConfig(
    criteria?.selectedDateRange || AlertDateRange.TODAY
  );

  if (criteria?.selectedAlertTypes?.length) {
    const queries = criteria.selectedAlertTypes.map((alertType) => {
      const field = `lastEventTimestampByEvent.${alertType}`;
      return createDateBetweenQuery(field, range);
    });
    return buildQueryTree(queries, 0, queries.length - 1, false);
  }
  return createDateBetweenQuery(AlertEntitiesQueryFieldType.TIMESTAMP, range);
};

const createAlertsSearchQuery = (
  fieldNames: {
    idFieldName: string;
    nameFieldName: string;
  },
  searchCriteria?: SearchCriteria
) => {
  if (!searchCriteria?.input) {
    return undefined;
  }
  return createSearchQuery(
    fieldNames,
    searchCriteria.searchType,
    searchCriteria.input
  );
};

const createEntitiesFilterSessionQuery = (
  criteria?: AlertListFilterSession
) => {
  return [
    criteria?.selectedAssetTypes?.length
      ? {
          qualifier: BDQueryCriteriaType.IN,
          field: AlertEntitiesQueryFieldType.SOURCE_TYPE,
          values: criteria.selectedAssetTypes,
        }
      : undefined,
    createEntitiesDateRangeQuery(criteria),
    createAlertsSearchQuery(
      {
        idFieldName: AlertEntitiesQueryFieldType.SOURCE_ID,
        nameFieldName: AlertEntitiesQueryFieldType.SOURCE_NAME,
      },
      criteria?.searchCriteria
    ),
  ];
};

export const createAlertsEntitiesQuery = (
  params: Partial<AlertParams>,
  criteria?: AlertListFilterSession
): BDQuery | undefined => {
  const queries: BDQuery[] = [
    params ? createEntitiesContextQuery(params) : undefined,
    ...createEntitiesFilterSessionQuery(criteria),
  ].filter((query) => !!query) as BDQuery[];
  if (!queries?.length) {
    return undefined;
  }
  return buildQueryTree(queries, 0, queries.length - 1, true);
};

const buildDriverUrl = (driversId: string) =>
  makeRoutePath(
    findRoute(RouteName.CS_PLATFORM_DRIVERS_DETAILS),
    { driversId },
    false,
    true
  );

export const getTirePressureAlert = (
  exp: number,
  act: number
): AlertEvent.LOW_TIRE_PRESSURE | AlertEvent.FLAT_TIRE | undefined => {
  const { lowTire, flatTire, tirePressure } = {
    lowTire: exp * TIRES_THRESHOLD_LOW,
    flatTire: exp * TIRES_THRESHOLD_FLAT,
    tirePressure: act * 1,
  };
  if (Number.isNaN(exp) || Number.isNaN(act)) {
    return undefined;
  }
  return tirePressure > lowTire
    ? undefined
    : tirePressure > flatTire
      ? AlertEvent.LOW_TIRE_PRESSURE
      : AlertEvent.FLAT_TIRE;
};

export const isTirePressuresMatched = (
  displayedAlertContentVariableMap: AlertDetailsMap,
  alertContentVariableMap: AlertDetailsMap,
  tireAlertEvent: AlertEvent.FLAT_TIRE | AlertEvent.LOW_TIRE_PRESSURE
): boolean => {
  const displayedAlertTires = {
    frontLeft:
      getTirePressureAlert(
        Number(displayedAlertContentVariableMap.tires_front_exp_kpa),
        Number(displayedAlertContentVariableMap.tire_driver_front_act_kpa)
      ) === tireAlertEvent
        ? tireAlertEvent
        : undefined,
    frontRight:
      getTirePressureAlert(
        Number(displayedAlertContentVariableMap.tires_front_exp_kpa),
        Number(displayedAlertContentVariableMap.tire_pass_front_act_kpa)
      ) === tireAlertEvent
        ? tireAlertEvent
        : undefined,
    rearLeft:
      getTirePressureAlert(
        Number(displayedAlertContentVariableMap.tires_rear_exp_kpa),
        Number(displayedAlertContentVariableMap.tire_driver_rear_act_kpa)
      ) === tireAlertEvent
        ? tireAlertEvent
        : undefined,
    rearRight:
      getTirePressureAlert(
        Number(displayedAlertContentVariableMap.tires_rear_exp_kpa),
        Number(displayedAlertContentVariableMap.tire_pass_rear_act_kpa)
      ) === tireAlertEvent
        ? tireAlertEvent
        : undefined,
  };

  const alertTires = {
    frontLeft:
      getTirePressureAlert(
        Number(alertContentVariableMap.tires_front_exp_kpa),
        Number(alertContentVariableMap.tire_driver_front_act_kpa)
      ) === tireAlertEvent
        ? tireAlertEvent
        : undefined,
    frontRight:
      getTirePressureAlert(
        Number(alertContentVariableMap.tires_front_exp_kpa),
        Number(alertContentVariableMap.tire_pass_front_act_kpa)
      ) === tireAlertEvent
        ? tireAlertEvent
        : undefined,
    rearLeft:
      getTirePressureAlert(
        Number(alertContentVariableMap.tires_rear_exp_kpa),
        Number(alertContentVariableMap.tire_driver_rear_act_kpa)
      ) === tireAlertEvent
        ? tireAlertEvent
        : undefined,
    rearRight:
      getTirePressureAlert(
        Number(alertContentVariableMap.tires_rear_exp_kpa),
        Number(alertContentVariableMap.tire_pass_rear_act_kpa)
      ) === tireAlertEvent
        ? tireAlertEvent
        : undefined,
  };

  return JSON.stringify(displayedAlertTires) === JSON.stringify(alertTires);
};

/**
 * Maps an alert event to a translated message with asset links.
 *
 * @param event - The alert event
 * @param translations - Translation strings
 * @param buildAssetUrl - Optional function to build asset URLs. If not provided,
 *                        falls back to legacy URL format. For org-based routing,
 *                        pass the function from `useBuildAssetUrl` hook.
 */
type MapEventToMessageEvent = {
  eventType?: AlertEvent | string;
  event?: { event?: AlertEvent | string };
  contentVariableMap?: AlertDetailsMap;
};

export const mapEventToMessage = (
  event: MapEventToMessageEvent | undefined,
  translations: typeof NOTIFICATION_SETTINGS_MESSAGES,
  buildAssetUrl: BuildAssetUrlFn = defaultBuildAssetUrl
) => {
  if (!event || !event.eventType) {
    return (
      <Trans
        i18nKey="settings:notifications.messages.unknownEventType"
        values={{ eventType: translateText('common:unknown') }}
      />
    );
  }

  const searchKey = event.eventType ? event.eventType : event?.event?.event;

  // Ensure contentVariableMap is always defined and safely destructured
  const contentVars = (event?.contentVariableMap || {}) as unknown as Record<
    string,
    string
  >;

  const createTransMessage = (
    i18nKey: string,
    linkHref: string,
    values: object
  ) => {
    return (
      <Trans
        i18nKey={i18nKey}
        components={{
          alink: <Link href={linkHref} rel="noopener noreferrer" />,
        }}
        values={values}
      />
    );
  };
  switch (searchKey) {
    case AlertEvent.ACCOUNT_UPDATE:
      return translations['settings:notifications.messages.profileUpdated'];

    case AlertEvent.BRAKE_PADS:
      return createTransMessage(
        'settings:notifications.messages.brake-pads',
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_objectid:
            contentVars.alert_objectid ||
            translateText('common:propertyState.notApplicable'),
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
          alert_object_fault_code:
            contentVars.alert_object_fault_code ||
            translateText('common:unknown'),
        }
      );

    case AlertEvent.OIL_LIFE:
      return createTransMessage(
        'settings:notifications.messages.oilLife',
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
          alert_object_alias_oil_life:
            contentVars.alert_object_alias_oil_life ||
            translateText('common:unknown'),
        }
      );

    case AlertEvent.FLAT_TIRE:
    case AlertEvent.LOW_TIRE_PRESSURE:
    case AlertEvent.LOW_BATTERY:
    case AlertEvent.LOW_WASHER_FLUID:
    case AlertEvent.ENGINE_AIR_FILTER:
      return createTransMessage(
        `settings:notifications.messages.${searchKey.toLowerCase()}`,
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
        }
      );

    case AlertEvent.ANTILOCK_BRAKING_SYSTEM:
    case AlertEvent.AIRBAG_SYSTEM:
    case AlertEvent.EMISSION_SYSTEM:
    case AlertEvent.ENGINE_AND_TRANSMISSION:
    case AlertEvent.LITHIUM_ION_BATTERY:
    case AlertEvent.LOW_BRAKE_FLUID:
    case AlertEvent.STABILITY_CONTROL_SYSTEM:
    case AlertEvent.ONSTAR_SYSTEM:
    case AlertEvent.ELECTRIC_DRIVE_SYSTEM: {
      const criticalKeySuffix =
        contentVars.alert_object_dtc_criticality === 'A critical issue'
          ? 'Critical'
          : 'NonCritical';

      const i18nKey = `settings:notifications.messages.${searchKey.toLowerCase()}${criticalKeySuffix}`;
      return createTransMessage(
        i18nKey,
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
          alert_object_fault_code:
            contentVars.alert_object_fault_code ||
            translateText('common:unknown'),
        }
      );
    }

    case AlertEvent.ADD_USER: {
      return (
        <UserAlertLabel
          userId={contentVars.alert_objectid}
          searchKey={searchKey}
          userFirstName={contentVars.alert_object_user_first_name}
          userLastName={contentVars.alert_object_user_last_name}
          userEmail={contentVars.alert_object_user_email}
        />
      );
    }

    case AlertEvent.REMOVE_USER: {
      return (
        <Trans
          i18nKey={`settings:notifications.messages.${searchKey.toLowerCase()}`}
          values={{
            alert_object_user_first_name:
              contentVars.alert_object_user_first_name ||
              translateText('common:propertyState.notApplicable'),
            alert_object_user_last_name:
              contentVars.alert_object_user_last_name ||
              translateText('common:propertyState.notApplicable'),
          }}
        />
      );
    }

    default:
      return (
        <Trans
          i18nKey="settings:notifications.messages.unknownEventType"
          values={{ eventType: searchKey || translateText('common:unknown') }}
        />
      );
  }
};

/**
 * Converts an alert event to a translated message with asset links.
 *
 * @param event - The alert event
 * @param translations - Translation strings
 * @param onClick - Optional click handler for the link
 * @param buildAssetUrl - Optional function to build asset URLs. If not provided,
 *                        falls back to legacy URL format. For org-based routing,
 *                        pass the function from `useBuildAssetUrl` hook.
 */
export const eventToMessage = (
  event: AlertDto | AlertRecord,
  translations: typeof NOTIFICATION_SETTINGS_MESSAGES,
  onClick?: () => void,
  buildAssetUrl: BuildAssetUrlFn = defaultBuildAssetUrl,
  timeZone?: string,
  locale?: string
) => {
  const searchKey = event?.event?.event ?? event?.lastGroupEvent?.event?.event;
  if (!searchKey) {
    return (
      <Trans
        i18nKey="settings:notifications.messages.unknownEventType"
        values={{ eventType: undefined }}
      />
    );
  }

  // Ensure contentVariableMap is always defined and safely destructured
  const contentVars = (event.event?.contentVariableMap ||
    {}) as unknown as Record<string, string>;

  const inactiveSinceFormatted = contentVars.inactive_since_timestamp_iso
    ? format(
        new Date(contentVars.inactive_since_timestamp_iso),
        DEFAULT_MONTH_DAY_YEAR_DATE_FORMAT,
        locale,
        timeZone
      )
    : DEFAULT_EMPTY_VALUE;

  const createTransMessage = (
    i18nKey: string,
    linkHref: string,
    values: object
  ) => {
    return (
      <Trans
        i18nKey={i18nKey}
        components={{
          alink: (
            <RouteLink
              onClick={(e) => {
                e.stopPropagation();
                onClick?.();
              }}
              to={linkHref}
            />
          ),
        }}
        values={values}
        parent="span"
        data-heap-redact-text
      />
    );
  };

  const createTransMessageWithLinks = (
    i18nKey: string,
    links: Record<string, string>,
    values: object
  ) => {
    return (
      <Trans
        i18nKey={i18nKey}
        components={Object.fromEntries(
          Object.entries(links).map(([name, href]) => [
            name,
            <RouteLink
              key={name}
              onClick={(e) => {
                e.stopPropagation();
                onClick?.();
              }}
              to={href}
            />,
          ])
        )}
        values={values}
        parent="span"
        data-heap-redact-text
      />
    );
  };

  switch (searchKey) {
    case AlertEvent.ACCOUNT_UPDATE:
      return translations['settings:notifications.messages.profileUpdated'];

    case AlertEvent.BRAKE_PADS:
      return createTransMessage(
        'settings:notifications.messages.brake-pads',
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_objectid:
            contentVars.alert_objectid ||
            translateText('common:propertyState.notApplicable'),
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
          alert_object_fault_code:
            contentVars.alert_object_fault_code ||
            translateText('common:unknown'),
        }
      );

    case AlertEvent.OIL_LIFE:
      return createTransMessage(
        'settings:notifications.messages.oilLife',
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
          alert_object_alias_oil_life:
            contentVars.alert_object_alias_oil_life || '0',
        }
      );

    case AlertEvent.DEPRECATED_ONBOARDING_VEHICLE:
      return createTransMessage(
        'settings:notifications.messages.vehicle-onboarding',
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_object_vehicle_description:
            contentVars.alert_object_vehicle_description ||
            translateText('common:unknown'),
          alert_objectid:
            contentVars.alert_objectid ||
            translateText('common:propertyState.notApplicable'),
        }
      );

    case AlertEvent.FLAT_TIRE:
    case AlertEvent.LOW_TIRE_PRESSURE:
    case AlertEvent.LOW_BATTERY:
    case AlertEvent.TWELVE_VOLT_BATTERY:
    case AlertEvent.LOW_WASHER_FLUID:
    case AlertEvent.ENGINE_AIR_FILTER:
      return createTransMessage(
        `settings:notifications.messages.${searchKey.toLowerCase()}`,
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
        }
      );

    case AlertEvent.ANTILOCK_BRAKING_SYSTEM:
    case AlertEvent.AIRBAG_SYSTEM:
    case AlertEvent.EMISSION_SYSTEM:
    case AlertEvent.ENGINE_AND_TRANSMISSION:
    case AlertEvent.LITHIUM_ION_BATTERY:
    case AlertEvent.LOW_BRAKE_FLUID:
    case AlertEvent.STABILITY_CONTROL_SYSTEM:
    case AlertEvent.ONSTAR_SYSTEM:
    case AlertEvent.ELECTRIC_DRIVE_SYSTEM: {
      const criticalKeySuffix =
        contentVars.alert_object_dtc_criticality === 'A critical issue'
          ? 'Critical'
          : 'NonCritical';

      const i18nKey = `settings:notifications.messages.${searchKey.toLowerCase()}${criticalKeySuffix}`;
      return createTransMessage(
        i18nKey,
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          alert_object_alias:
            contentVars.alert_object_alias || translateText('common:unknown'),
          alert_object_fault_code:
            contentVars.alert_object_fault_code ||
            translateText('common:unknown'),
        }
      );
    }

    case AlertEvent.ADD_USER: {
      return (
        <UserAlertLabel
          userId={contentVars.alert_objectid}
          searchKey={searchKey}
          userFirstName={contentVars.alert_object_user_first_name}
          userLastName={contentVars.alert_object_user_last_name}
          userEmail={contentVars.alert_object_user_email}
          onClick={onClick}
        />
      );
    }

    case AlertEvent.REMOVE_USER: {
      return (
        <Trans
          i18nKey={`settings:notifications.messages.${searchKey.toLowerCase()}`}
          values={{
            alert_object_user_first_name:
              contentVars.alert_object_user_first_name ||
              translateText('common:propertyState.notApplicable'),
            alert_object_user_last_name:
              contentVars.alert_object_user_last_name ||
              translateText('common:propertyState.notApplicable'),
          }}
        />
      );
    }

    case AlertEvent.CAMERA_ADDED:
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        FLEET_PERIPHERALS_LIST,
        {
          alert_object_cameraId: contentVars.camera_id || DEFAULT_EMPTY_VALUE,
          alert_object_fleet_name:
            contentVars.fleet_name || DEFAULT_EMPTY_VALUE,
        }
      );
    case AlertEvent.CAMERA_DELETED:
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        FLEET_PERIPHERALS_LIST,
        {
          alert_object_cameraId: contentVars.camera_id || DEFAULT_EMPTY_VALUE,
          alert_object_fleet_name:
            contentVars.fleet_name || DEFAULT_EMPTY_VALUE,
        }
      );
    case AlertEvent.ADMIN_PIN_CHANGED:
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        SETTINGS_VIEW_PATH,
        {}
      );
    case AlertEvent.LENS_OBSTRUCTED:
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        FLEET_PERIPHERALS_LIST,
        {
          alert_object_alias:
            contentVars.alert_object_alias || DEFAULT_EMPTY_VALUE,
        }
      );
    case AlertEvent.LOW_TRIP_SCORE: {
      if (contentVars.driver_id) {
        return createTransMessage(
          `settings:notifications.messages.${searchKey}`,
          buildDriverUrl(contentVars.driver_id || ''),
          {
            driver_full_name:
              contentVars.driver_full_name || DEFAULT_EMPTY_VALUE,
          }
        );
      }
      if (contentVars.alert_objectid) {
        return createTransMessage(
          `settings:notifications.messages.${searchKey}-vehicle`,
          buildAssetUrl(contentVars.alert_objectid || ''),
          {
            alert_object_alias:
              contentVars.alert_object_alias || DEFAULT_EMPTY_VALUE,
          }
        );
      }
      return createTransMessage(
        `settings:notifications.messages.${searchKey}-no-id`,
        '',
        {}
      );
    }
    case AlertEvent.SAFETY_EVENT: {
      if (contentVars.driver_id && contentVars.alert_objectid) {
        return createTransMessageWithLinks(
          `settings:notifications.messages.${searchKey}-driver-vehicle`,
          {
            driverLink: buildDriverUrl(contentVars.driver_id),
            vehicleLink: buildAssetUrl(contentVars.alert_objectid),
          },
          {
            subType: contentVars.safety_event_name_en || DEFAULT_EMPTY_VALUE,
            driver_full_name:
              contentVars.driver_full_name || DEFAULT_EMPTY_VALUE,
            alert_object_alias:
              contentVars.alert_object_alias || DEFAULT_EMPTY_VALUE,
          }
        );
      }
      if (contentVars.driver_id) {
        return createTransMessage(
          `settings:notifications.messages.${searchKey}`,
          buildDriverUrl(contentVars.driver_id || ''),
          {
            subType: contentVars.safety_event_name_en || DEFAULT_EMPTY_VALUE,
            alert_object_alias:
              contentVars.driver_full_name ||
              contentVars.alert_object_alias ||
              DEFAULT_EMPTY_VALUE,
          }
        );
      }
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        buildAssetUrl(contentVars.alert_objectid || ''),
        {
          subType: contentVars.safety_event_name_en || DEFAULT_EMPTY_VALUE,
          alert_object_alias:
            contentVars.driver_name ||
            contentVars.alert_object_alias ||
            DEFAULT_EMPTY_VALUE,
        }
      );
    }
    case AlertEvent.AUTOMATIC_CRASH_RESPONSE:
    case AlertEvent.LOW_LEVEL_COLLISION:
    case AlertEvent.THERMAL_EVENT:
    case AlertEvent.THEFT_ALARM:
      return getEmerEventMessage(
        searchKey,
        contentVars,
        { buildAssetUrl, buildDriverUrl },
        onClick
      );
    case AlertEvent.CAMERA_NOT_RECORDING:
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        FLEET_PERIPHERALS_LIST,
        {
          alert_object_alias:
            contentVars.alert_object_alias || DEFAULT_EMPTY_VALUE,
        }
      );
    case AlertEvent.CAMERA_OFFLINE:
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        FLEET_PERIPHERALS_LIST,
        {
          alert_object_alias:
            contentVars.alert_object_alias || DEFAULT_EMPTY_VALUE,
          alert_object_event_timestamp: contentVars.eventTimestamp,
        }
      );
    // Temporarily adding the deprecated vehicle offboarding event mapping to the alerts to prevent unknown alert event type messages.
    // TODO: Remove the deprecated vehicle offboarding event once we confirm the events are no longer used in the backend service and deleted from the databases.
    case AlertEvent.DEPRECATED_OFFBOARDING_VEHICLE: {
      return (
        <TranslatedLabel
          translationKey="settings:notifications.messages.vehicle-offboarding"
          typographyVariant="body_regular"
          variables={{
            vehicleName:
              contentVars.alert_object_vehicle_name ||
              contentVars.alert_objectid,
          }}
        />
      );
    }
    case AlertEvent.BATCH_OFFBOARDING: {
      const count = Number(contentVars.alert_object_batch_completed_count) || 1;
      const vehicleName =
        contentVars.alert_object_vehicle_name || DEFAULT_EMPTY_VALUE;
      const fleetName =
        contentVars.alert_object_batch_fleet_name ||
        translateText('settings:notifications.messages.unknownFleet');
      const fleetCount =
        Number(contentVars.alert_object_batch_fleet_count) || 1;

      // Case 1: Single fleet
      // Only show vehicleName when count === 1 (single vehicle from single fleet)
      // Otherwise show count with plural form
      if (fleetCount === 1) {
        return (
          <TranslatedLabel
            translationKey="settings:notifications.messages.batch-offboarding"
            typographyVariant="body_regular"
            variables={{
              vehicleName,
              fleetName,
              count,
            }}
          />
        );
      }

      // Case 2: 2-3 fleets - display one line per fleet
      // Each line shows count (not vehicleName), even if count is 1
      if (fleetCount >= 2 && fleetCount <= 3) {
        // Parse fleet details from alert_object_batch_offboarding
        // Expected format: "2 vehicles deleted from FleetA<br>4 vehicles deleted from FleetB"
        const batchOffboardingStr = contentVars.alert_object_batch_offboarding;
        let fleetDetails: Array<{
          fleetName: string;
          count: number;
        }> = [];

        if (batchOffboardingStr) {
          // Split by <br> to get individual lines
          const lines = batchOffboardingStr.split('<br>');

          // Parse each line using regex to extract count and fleet name
          // Pattern: "{count} vehicle(s) deleted from {fleetName}."
          // Also handles optional period at the end
          const pattern = /(\d+)\s+vehicles?\s+deleted\s+from\s+(.+?)\.?$/i;

          fleetDetails = lines
            .map((line) => {
              const match = line.trim().match(pattern);
              if (match) {
                return {
                  count: Number(match[1]),
                  fleetName: match[2].trim(),
                };
              }
              return null;
            })
            .filter(
              (detail): detail is { fleetName: string; count: number } =>
                detail !== null
            );
        }

        // If no fleet details parsed, use fallback
        if (fleetDetails.length === 0) {
          fleetDetails = [{ fleetName, count }];
        }

        return (
          <>
            {fleetDetails.map((fleet) => (
              <div key={`${fleet.fleetName}-${fleet.count}`}>
                <TranslatedLabel
                  translationKey="settings:notifications.messages.batch-offboarding-multi-fleet"
                  typographyVariant="body_regular"
                  variables={{
                    fleetName: fleet.fleetName,
                    count: fleet.count,
                  }}
                />
              </div>
            ))}
          </>
        );
      }

      // Case 3: 4+ fleets - display aggregate message
      return (
        <TranslatedLabel
          translationKey="settings:notifications.messages.batch-offboarding-multiple-fleets"
          typographyVariant="body_regular"
          variables={{
            count,
            fleetName,
          }}
        />
      );
    }
    case AlertEvent.BATCH_ONBOARDING: {
      // Dynamically construct the batch summary details link
      const batchDetailsRoute = findRoute(
        RouteName.ORGANIZATIONS_VEHICLES_BATCH
      );
      const batchDetailsLink = makeRoutePath(batchDetailsRoute, {
        organizationsId: contentVars.alert_object_organization_id,
        batchId: contentVars.alert_objectid,
      });

      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        batchDetailsLink,
        {
          alert_object_batch_upload_type:
            contentVars.alert_object_batch_upload_type?.toLowerCase() ||
            'generic',
          alert_object_batch_created_by:
            contentVars.alert_object_batch_created_by || DEFAULT_EMPTY_VALUE,
        }
      );
    }
    case AlertEvent.LOCATION_MASK_UNMASK:
    case AlertEvent.VEHICLE_DRIVE_BLOCK: {
      const actionKey = contentVars.action || 'unknown';
      return createTransMessage(
        `settings:notifications.messages.${searchKey}-${actionKey}`,
        buildAssetUrl(contentVars.vin || ''),
        {
          alert_object_vin: contentVars.vin || DEFAULT_EMPTY_VALUE,
        }
      );
    }
    case AlertEvent.GEOFENCE_ENTRY_EXIT: {
      const enteredOrExited = contentVars.entered_or_exited || 'unknown';
      return (
        <TranslatedLabel
          translationKey={`settings:notifications.messages.${searchKey}-${enteredOrExited}`}
          typographyVariant="body_regular"
          variables={{
            alert_object_vin: contentVars.vin || DEFAULT_EMPTY_VALUE,
            geofence_name: contentVars.geofence_name || DEFAULT_EMPTY_VALUE,
          }}
        />
      );
    }
    case AlertEvent.VEHICLE_OFF_HOURS: {
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        buildAssetUrl(contentVars.vin || ''),
        {
          alert_object_vin: contentVars.vin || DEFAULT_EMPTY_VALUE,
        }
      );
    }
    case AlertEvent.VEHICLE_INACTIVITY: {
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        buildAssetUrl(contentVars.vin || ''),
        {
          alert_object_vin: contentVars.vin || DEFAULT_EMPTY_VALUE,
          alert_object_inactive_since: inactiveSinceFormatted,
        }
      );
    }
    case AlertEvent.VEHICLE_IDLING: {
      return createTransMessage(
        `settings:notifications.messages.${searchKey}`,
        buildAssetUrl(contentVars.vin || ''),
        {
          alert_object_vin: contentVars.vin || DEFAULT_EMPTY_VALUE,
        }
      );
    }
    case AlertEvent.VIDEO_CLIP_REQUEST_STATUS: {
      const clipStatus = (contentVars.clip_status || '').toUpperCase();
      const detailsRoute = findRoute(
        RouteName.ORGANIZATIONS_CUSTOM_VIDEO_DETAILS
      );
      const clipDetailsLink = makeRoutePath(detailsRoute, {
        organizationsId: contentVars.organization_id,
        clipId: contentVars.clip_id,
      });
      const clipNameValues = {
        clip_name: contentVars.clip_name || DEFAULT_EMPTY_VALUE,
      };

      switch (clipStatus) {
        case 'READY':
          return createTransMessage(
            `settings:notifications.messages.${searchKey}-ready`,
            clipDetailsLink,
            clipNameValues
          );
        case 'PROCESSING':
        case 'PENDING':
          return createTransMessage(
            `settings:notifications.messages.${searchKey}-processing`,
            clipDetailsLink,
            clipNameValues
          );
        case 'EXPIRED':
          return createTransMessage(
            `settings:notifications.messages.${searchKey}-expired`,
            clipDetailsLink,
            clipNameValues
          );
        case 'FAILED':
          return createTransMessage(
            `settings:notifications.messages.${searchKey}-failed`,
            clipDetailsLink,
            clipNameValues
          );
        default:
          return (
            <Trans
              i18nKey="settings:notifications.messages.unknownEventType"
              values={{ eventType: undefined }}
            />
          );
      }
    }
    default:
      return (
        <Trans
          i18nKey="settings:notifications.messages.unknownEventType"
          values={{ eventType: undefined }}
        />
      );
  }
};
